// File: src/components/ImageGenerationHistoryDialog.tsx
import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ImageGeneration, Product } from '../../types';

interface ImageGenerationHistoryDialogProps {
    open: boolean;
    onClose: () => void;
    historyGenerations: ImageGeneration[];
    product?: Product;
    onLoadGeneration: (gen: ImageGeneration) => void;
    isLoading?: boolean;
}

const ImageGenerationHistoryDialog: React.FC<ImageGenerationHistoryDialogProps> = ({
                                                                                       open,
                                                                                       onClose,
                                                                                       historyGenerations,
                                                                                       product,
                                                                                       onLoadGeneration,
                                                                                       isLoading = false
                                                                                   }) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Image Generation History for {product?.name}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : historyGenerations.length === 0 ? (
                    <Typography>No image generations found for this product.</Typography>
                ) : (
                    <Box>
                        {historyGenerations.map((gen: ImageGeneration) => (
                            <Card
                                key={gen.id}
                                variant="outlined"
                                sx={{ mb: 2, cursor: 'pointer' }}
                                onClick={() => onLoadGeneration(gen)}
                            >
                                <CardContent>
                                    <Typography variant="subtitle1">
                                        Generation #{gen.iteration_number} - Status: <Chip label={gen.status} size="small" color={gen.status === 'completed' ? 'success' : 'info'}/>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Created: {new Date(gen.created_at).toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        AI Service for Prompt: {gen.ai_service_used}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        AI Service for Image: {gen.image_ai_service_used}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Images: {(gen.image_requests || []).length}
                                    </Typography>
                                    {gen.reference_image_url && (
                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" color="text.secondary">Based on:</Typography>
                                            <img src={gen.reference_image_url} alt="reference" style={{ width: 40, height: 40, borderRadius: 1 }} />
                                        </Box>
                                    )}
                                    {/* <-- Исправлено: Возвращен блок с миниатюрами результатов */}
                                    {gen.image_requests && gen.image_requests.length > 0 && (
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="caption" color="text.secondary">Results:</Typography>
                                            {gen.image_requests.slice(0, 3).map((req, i) => req.generated_image_url ? (
                                                <img
                                                    key={i}
                                                    src={req.generated_image_url}
                                                    alt={`Result ${i}`}
                                                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                                                />
                                            ) : null)}
                                            {gen.image_requests.length > 3 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    +{gen.image_requests.length - 3} more
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ImageGenerationHistoryDialog;